// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { loadMedicalWishes, loadPersonalFarewell, loadMessages } from '@/services/wishes'
import { loadTrustedPersons } from '@/services/trustedPerson'
import { loadLivingWillDetails } from '@/services/wizard'
import { ROUTES } from '@/lib/constants/routes'

/**
 * Regiecoach - rustige, deterministische begeleiding.
 * Suggesties zijn altijd gebaseerd op reeds ingevulde gegevens, nooit willekeurig.
 * Geen AI, geen chatbot, geen oordeel. Read-only afgeleid; geen datamodelwijziging.
 */
export interface CoachSuggestion { label: string; route: string }
export type CoachPage = 'partner' | 'notarial' | 'business' | 'medical' | 'digital' | 'farewell'

interface CoachContext {
  hasTrusted:       boolean
  hasLivingWill:    boolean
  hasFarewell:      boolean
  hasMessages:      boolean
  hasMedicalWishes: boolean
  gpKnown:          boolean
}

async function loadContext(userId: string): Promise<CoachContext> {
  const [medical, trusted, livingWill, farewell, messages] = await Promise.all([
    loadMedicalWishes(userId), loadTrustedPersons(userId), loadLivingWillDetails(userId),
    loadPersonalFarewell(userId), loadMessages(userId),
  ])
  const m = medical
  const has = (v?: string) => !!(v && v.trim())
  return {
    hasTrusted:       (trusted?.length ?? 0) > 0,
    hasLivingWill:    !!(livingWill && (livingWill.notarisName || livingWill.documentLocation || livingWill.year)) || m?.livingWillExists === 'yes',
    hasFarewell:      !!farewell,
    hasMessages:      (messages?.length ?? 0) > 0,
    hasMedicalWishes: has(m?.treatmentPreferences) || has(m?.resuscitation) || has(m?.additionalWishes),
    gpKnown:          has(m?.gpName),
  }
}

export async function loadCoachSuggestions(userId: string, page: CoachPage): Promise<CoachSuggestion[]> {
  try {
    const c = await loadContext(userId)
    const s: CoachSuggestion[] = []

    switch (page) {
      case 'partner':
        if (!c.hasFarewell) s.push({ label: 'Een persoonlijke boodschap voor uw partner vastleggen', route: ROUTES.WISHES_FAREWELL })
        if (!c.hasTrusted)  s.push({ label: 'Een vertrouwenspersoon aanwijzen', route: ROUTES.TRUSTED_PERSON })
        break
      case 'notarial':
        if (!c.hasFarewell) s.push({ label: 'Persoonlijke berichten voor uw naasten vastleggen', route: ROUTES.WISHES_FAREWELL })
        if (!c.hasTrusted)  s.push({ label: 'Een vertrouwenspersoon aanwijzen', route: ROUTES.TRUSTED_PERSON })
        break
      case 'business':
        s.push({ label: 'Nadenken over bedrijfsopvolging en uw notariële wensen', route: ROUTES.NOTARIAL })
        s.push({ label: 'Vastleggen waar uw bedrijfsdocumenten te vinden zijn', route: ROUTES.DOCUMENTS })
        break
      case 'medical':
        if (!c.hasTrusted)    s.push({ label: 'Een vertrouwenspersoon aanwijzen die uw wensen kent', route: ROUTES.TRUSTED_PERSON })
        if (!c.hasLivingWill) s.push({ label: 'Nadenken over een levenstestament', route: ROUTES.NOTARIAL })
        s.push({ label: 'Informatie over euthanasie lezen', route: ROUTES.EUTHANASIE_INFO })
        break
      case 'digital':
        if (!c.hasTrusted) s.push({ label: 'Een vertrouwenspersoon aanwijzen die dit later afhandelt', route: ROUTES.TRUSTED_PERSON })
        s.push({ label: 'Vastleggen waar uw belangrijke documenten staan', route: ROUTES.DOCUMENTS })
        break
      case 'farewell':
        if (!c.hasMessages) s.push({ label: 'Een bericht via de Berichtenservice vastleggen', route: ROUTES.MESSAGES })
        if (!c.hasTrusted)  s.push({ label: 'Een vertrouwenspersoon aanwijzen', route: ROUTES.TRUSTED_PERSON })
        break
    }

    return s.slice(0, 3) // rust: nooit meer dan drie suggesties
  } catch {
    return []
  }
}
